/**
 * ✉ 딜등록 정보 요청 창 (v2.141.0 · 2026-09-08 / 담당자는 거래처 담당자 주소록에서 v2.143.0)
 *
 * 제안사(Tier 2)에게 「딜등록정보요청」 양식을 보내는 자리. 담당자를 주소록에서 고르거나 직접 적고,
 * 우리가 아는 값(엔드유저 · 프로젝트명 · 진행 현황)을 적으면 —
 *   서버가 고정 안내문(초안)과 채워진 양식(xlsx)을 만들어 주고 → 포털 메일 창이 **받는사람·제목·본문·첨부가
 *   채워진 채** 뜬다 → 사람이 고쳐서 보내면 → 요청 기록이 남는다([✉ 정보 요청] 탭).
 *
 * ⚠이 창은 「메일 창을 열어 주는」 것까지만 한다. 발송은 메일 창이, 기록은 발송 성공 뒤(onSent)에 남긴다 —
 *   보내지 않고 닫으면 아무것도 남지 않는다(「보냈다」 기록이 거짓이 되지 않게).
 * ⚠배경을 눌러도 닫히지 않는다 — 닫기는 [취소]·× 로만 (`check:modal`).
 */

import { useMemo, useState } from 'react';
import {
  getRequestDraft, fetchRequestForm, createRequest,
  type DealRequestInput, type DealRequestRow,
} from '../lib/deal-registration-api';
import type { PartnerContact } from '../lib/partner-contact-api';
import { mailSubject } from '../lib/mail-subject';
import { openMailCompose } from '../lib/mail-compose';

export interface DealRequestDialogProps {
  /** 거래처 담당자 주소록 — 메일 주소가 있는 사람이 받는사람 후보 */
  contacts: PartnerContact[];
  /** 미리 채울 값 — 기존 딜·주소록 담당자·보낸 요청에서 열 때 */
  initial: Partial<DealRequestInput>;
  /** 기존 딜에서 열었으면 그 번호 — 요청 기록이 그 딜에 연결된다 */
  dealId: number | null;
  onClose: () => void;
  /** 메일이 나가고 기록까지 남은 뒤 */
  onSent: (row: DealRequestRow) => void;
}

function emptyInput(): DealRequestInput {
  return {
    vendor: 'infortrend', contactId: null, company: '', contactName: '', email: '', phone: '',
    endUser: '', projectName: '', projectStatus: '',
  };
}

export function DealRequestDialog({ contacts, initial, dealId, onClose, onSent }: DealRequestDialogProps): React.JSX.Element {
  const [form, setForm] = useState<DealRequestInput>({ ...emptyInput(), ...initial });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const options = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return contacts
      .filter((c) => (c.isActive && c.email) || c.id === form.contactId)
      .filter((c) => !q || `${c.companyName} ${c.companyNameEn} ${c.name} ${c.title} ${c.email}`.toLowerCase().includes(q))
      .slice(0, 300);
  }, [contacts, filter, form.contactId]);

  const pickContact = (id: number | null): void => {
    const c = contacts.find((x) => x.id === id) ?? null;
    if (!c) { setForm({ ...form, contactId: null }); return; }
    setForm({ ...form, contactId: c.id, company: c.companyName, contactName: c.label, email: c.email, phone: c.mobile || c.phone });
  };

  const openMail = async (): Promise<void> => {
    setError('');
    if (!form.company.trim() && !form.email.trim()) {
      setError('회사 이름이나 담당자 메일 주소 중 하나는 적어 주세요.');
      return;
    }
    setBusy(true);
    try {
      const [draft, file] = await Promise.all([getRequestDraft(form), fetchRequestForm(form)]);
      const snapshot = { ...form };
      openMailCompose({
        to: draft.to,
        subject: mailSubject('딜등록 정보 요청', draft.subjectName),
        body: draft.body,
        files: [file],
        onSent: (res) => {
          createRequest({ ...snapshot, toEmails: res.to, dealId })
            .then(onSent)
            .catch((e: Error) => window.alert(`메일은 나갔지만 요청 기록을 남기지 못했습니다 — ${e.message}`));
        },
      });
      onClose();
    } catch (e) {
      setError(`메일 초안을 만들지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[85] flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-2xl mt-8">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center">
          ✉ 딜등록 정보 요청 {dealId ? <span className="ml-2 text-xs font-normal text-slate-500">— 이 딜등록에 연결됩니다</span> : null}
          <button type="button" onClick={onClose} disabled={busy} className="ui-close ml-auto" aria-label="닫기">×</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2 leading-relaxed">
            제안사 담당자에게 <b>고정 안내문</b>과 우리가 아는 값이 채워진 <b>「딜등록정보요청」 양식(엑셀)</b>을 보냅니다.
            메일 창에서 받는사람·본문을 고칠 수 있고, 보내면 [✉ 정보 요청] 목록에 기록됩니다. 회신이 오면 거기서 [딜등록으로 만들기].
          </div>

          <div>
            <span className="block text-xs text-slate-500 mb-1">제안사 담당자 <span className="text-slate-400">(거래처 담당자 주소록 — 메일 주소가 있는 사람 · 고르면 아래 칸이 채워집니다)</span></span>
            <div className="flex gap-2">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="회사·이름 찾기"
                className="ui-input w-40"
              />
              <select
                value={form.contactId ?? ''}
                onChange={(e) => pickContact(e.target.value ? Number(e.target.value) : null)}
                className="ui-input flex-1"
              >
                <option value="">직접 입력</option>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName}{c.label ? ` · ${c.label}` : ''}{c.email ? ` · ${c.email}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <label className="block md:col-span-1">
              <span className="block text-xs text-slate-500 mb-1">업체명 *</span>
              <input value={form.company} maxLength={200} onChange={(e) => setForm({ ...form, company: e.target.value })} className="ui-input w-full" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">담당자명</span>
              <input value={form.contactName} maxLength={100} onChange={(e) => setForm({ ...form, contactName: e.target.value })} className="ui-input w-full" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">이메일 <span className="text-slate-400">(받는사람)</span></span>
              <input value={form.email} maxLength={200} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" className="ui-input w-full" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">전화번호</span>
              <input value={form.phone} maxLength={50} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ui-input w-full" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">엔드유저명 <span className="text-slate-400">(알면)</span></span>
              <input value={form.endUser} maxLength={2000} onChange={(e) => setForm({ ...form, endUser: e.target.value })} placeholder="예: 서울특별시 소방재난본부" className="ui-input w-full" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">프로젝트명 <span className="text-slate-400">(알면)</span></span>
              <input value={form.projectName} maxLength={1000} onChange={(e) => setForm({ ...form, projectName: e.target.value })} placeholder="예: 2026년 한강교량 방범 CCTV 구축" className="ui-input w-full" />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs text-slate-500 mb-1">프로젝트 진행 현황 <span className="text-slate-400">(알면 — 제안사가 고쳐 적습니다)</span></span>
            <textarea value={form.projectStatus} rows={2} maxLength={4000} onChange={(e) => setForm({ ...form, projectStatus: e.target.value })} placeholder="예: EMC 로 설계된 사업 · 스펙 충족되는 인포트렌드 제품으로 입찰 참가 예정" className="ui-input ui-textarea w-full" />
          </label>

          <div className="text-xs text-slate-500">
            제안 모델 · 매니저 코멘트 · 애플리케이션은 제안사가 양식에 적는 자리라 여기서는 비워 보냅니다.
            {!form.email.trim() ? <span className="text-amber-700"> ⚠ 메일 주소가 비어 있어 받는사람 없이 창이 뜹니다 — 창에서 적어 주세요.</span> : null}
          </div>

          {error ? <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">{error}</div> : null}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="btn btn-secondary">취소</button>
          <button type="button" onClick={() => void openMail()} disabled={busy} className="btn btn-primary">
            {busy ? '양식 만드는 중…' : '✉ 메일 창 열기'}
          </button>
        </div>
      </div>
    </div>
  );
}
